import React from "react";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveDocuments,
} from "@planzone/view-models";
import { Identifiable, Document } from "@planzone/dto";
import { DocumentIcon } from "@common/documentIcon";
import { FolderIcon } from "@common/folderIcon";
import { useWorkspaceContext } from "@scenes/workspaceContext";
import { Props } from "./types";
import { Loader } from "./loader";
import styles from "./styles.scss";

function isDocImage({ mimeType }: Identifiable<Document>): boolean {
  const imageMimeTypes = new Set([
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/bmp",
    "image/webp",
    "image/svg+xml",
    "image/tiff",
    "image/x-icon",
    "image/heif",
    "image/heic",
    "image/vnd.microsoft.icon",
    "image/x-ms-bmp",
    "image/avif",
    "image/jp2",
    "image/jxr",
  ]);

  return imageMimeTypes.has(mimeType.toLowerCase());
}

function DirectoryStructure({ folder: f, depth = 0, select }: Props) {
  const ctx = useWorkspaceContext();
  const { planzone } = useRetrieveCurrentPlanzone();
  const [isOpen, setIsOpen] = React.useState(true);
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    folder: rootFolder,
    retrieveDocuments,
  } = useRetrieveDocuments(
    ctx.type === "project" ? ctx.project.uuid : planzone.uuid,
    ctx.type === "project" ? "project" : "planzone",
  );
  const folder = f || rootFolder;

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveDocuments({
        namespace: ctx.type === "project" ? ctx.project.uuid : planzone.uuid,
        type: ctx.type === "project" ? "project" : "planzone",
      });
  }, []);

  return isRequestPending ? (
    <Loader />
  ) : (
    <div className={styles.directoryStructure}>
      {!!f && (
        <div
          className={styles.folder}
          style={{ paddingLeft: `${depth || 1}rem` }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <FolderIcon className={styles.folderIcon} isOpen={isOpen} />
          <p>{folder.name}</p>
        </div>
      )}
      {isOpen &&
        folder.children
          .filter((item) => item.type === "folder" || isDocImage(item))
          .map((item) =>
            item.type === "folder" ? (
              <DirectoryStructure
                key={item.uuid}
                folder={item}
                depth={depth + 1}
                select={select}
              />
            ) : (
              <div
                key={item.uuid}
                className={styles.document}
                style={{ paddingLeft: `${depth === 0 ? 1 : depth + 1}rem` }}
                onClick={() => select(item)}
              >
                <DocumentIcon
                  className={styles.documentIcon}
                  mimeType={item.mimeType}
                />
                <p>{item.name}</p>
              </div>
            ),
          )}
    </div>
  );
}

export { DirectoryStructure };
